import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.10:8005';
const DEVICE_ID_STORAGE_KEY = 'device_id';

const stableHash = (value) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
};

const getOrCreateDeviceId = () => {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;
  const generated = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
  return generated;
};

const buildDeviceHeaders = () => {
  const fingerprintSeed = [
    navigator.userAgent || '',
    navigator.language || '',
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    navigator.platform || '',
    `${navigator.hardwareConcurrency || 0}`,
  ].join('|');
  return {
    'X-Device-Id': getOrCreateDeviceId(),
    'X-Device-Fingerprint': stableHash(fingerprintSeed),
  };
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration (not failed login attempts)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isAuthAttempt =
      requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

    if (status === 401 && !isAuthAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', new URLSearchParams({ username: email, password }), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...buildDeviceHeaders(),
      }
    }),
  register: (userData) => api.post('/auth/register', userData),
  googleLogin: (token) =>
    api.post('/auth/google-login', null, {
      params: { token },
      headers: buildDeviceHeaders(),
    }),
};

export const attendanceAPI = {
  myFactories: () => api.get('/attendance/my-factories'),
  employeeProfile: () => api.get('/attendance/employee-profile'),
  punchStatus: (factoryId) => api.get('/attendance/punch/status', { params: { factory_id: factoryId } }),
  punchActiveSession: () => api.get('/attendance/punch/active-session'),
  recentPunchSessions: (factoryId, limit = 10) =>
    api.get('/attendance/punch/recent-sessions', { params: { factory_id: factoryId, limit } }),
  checkIn: (factoryId, lat, lng) => api.post('/attendance/punch/check-in', { factory_id: factoryId, lat, lng }),
  checkOut: (factoryId, lat, lng) => api.post('/attendance/punch/check-out', { factory_id: factoryId, lat, lng }),
  supervisorToday: (factoryId, day) =>
    api.get('/attendance/supervisor/today', { params: { factory_id: factoryId, day } }),
  supervisorTeamBoard: (params) => api.get('/attendance/supervisor/team-board', { params }),
  supervisorTeamDevices: (params) => api.get('/attendance/supervisor/team-devices', { params }),
  monthlyExport: (factoryId, year, month) =>
    api.get('/attendance/reports/monthly-export', {
      params: { factory_id: factoryId, year, month },
      responseType: 'blob',
    }),
};

export const dmAPI = {
  contacts: () => api.get('/dm/contacts'),
  messages: (withUserId, factoryId) =>
    api.get('/dm/messages', { params: { with_user_id: withUserId, factory_id: factoryId } }),
  send: (payload) => api.post('/dm/messages', payload),
  broadcastEligible: (factoryId, recipientScope) =>
    api.get('/dm/broadcast-eligible', { params: { factory_id: factoryId, recipient_scope: recipientScope } }),
  broadcast: (payload) => api.post('/dm/broadcast', payload),
};

export const managementAPI = {
  companies: () => api.get('/management/companies'),
  createCompany: (payload) => api.post('/management/companies', payload),
  employeesPageBundle: (companyId) =>
    api.get('/management/employees-page-bundle', { params: { company_id: companyId } }),
  employeesPageBundleLight: (companyId) =>
    api.get('/management/employees-page-bundle-light', { params: { company_id: companyId } }),
  employeeShiftsDetail: (employeeUserId, siteId) =>
    api.get('/management/employee-shifts-detail', { 
      params: { employee_user_id: employeeUserId, site_id: siteId } 
    }),
  employees: (companyId) => api.get('/management/employees', { params: { company_id: companyId } }),
  createEmployee: (payload) => api.post('/management/employees', payload),
  updateEmployee: (userId, companyId, payload) =>
    api.put(`/management/employees/${userId}`, payload, { params: { company_id: companyId } }),
  deactivateEmployee: (userId, companyId) =>
    api.delete(`/management/employees/${userId}`, { params: { company_id: companyId } }),
  factories: (companyId) => api.get('/management/factories', { params: companyId ? { company_id: companyId } : {} }),
  createFactory: (payload) => api.post('/management/factories', payload),
  updateFactory: (factoryId, payload) => api.put(`/management/factories/${factoryId}`, payload),
  createShift: (payload) => api.post('/management/shifts', payload),
  updateShift: (shiftId, payload) => api.put(`/management/shifts/${shiftId}`, payload),
  /** @param {{ contract_id?: number, company_id?: number }} params */
  listShifts: (params) => api.get('/management/shifts', { params }),
  contracts: (companyId) =>
    api.get('/management/contracts', { params: { company_id: companyId } }),
  createContract: (payload) => api.post('/management/contracts', payload),
  patchContract: (contractId, payload) =>
    api.patch(`/management/contracts/${contractId}`, payload),
  employeesDirectory: () => api.get('/management/employees-directory'),
  assignFactoryEmployee: (payload) => api.post('/management/factory-employees', payload),
  assignFactoryEmployeesBulk: (payload) => api.post('/management/factory-employees/bulk', payload),
  updateFactoryEmployee: (factoryEmployeeId, payload) =>
    api.put(`/management/factory-employees/${factoryEmployeeId}`, payload),
  assignEmployeeShift: (payload) => api.post('/management/employee-shifts', payload),
  employeeShifts: (factoryEmployeeId) => api.get('/management/employee-shifts', { params: { factory_employee_id: factoryEmployeeId } }),
  deleteEmployeeShift: (employeeShiftId) => api.delete(`/management/employee-shifts/${employeeShiftId}`),
  factoryEmployees: (factoryId) => api.get('/management/factory-employees', { params: { factory_id: factoryId } }),
  supervisorAssignments: (factoryId) => api.get('/management/supervisor-assignments', { params: { factory_id: factoryId } }),
  createSupervisorAssignment: (payload) => api.post('/management/supervisor-assignments', payload),
  deleteSupervisorAssignment: (assignmentId) => api.delete(`/management/supervisor-assignments/${assignmentId}`),
};

export default api;